import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, FormControlLabel, MenuItem, Paper,
  Stack, TextField, Typography,
} from '@mui/material';
import { AddRounded, AutoAwesomeRounded, CheckCircle, CloseRounded, Groups, ImageRounded, MeetingRoom, PersonSearch, Shuffle } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addService, fetchServices } from '../../../firebase/firebase-delegate';
import { TIMEZONES } from '../../../constants';
import { PublicBusinessHours, SaveMyOnboardingModeInput, TenantOnboardingStatus } from '../../../sitePublishing/types';
import {
  useChooseOnboardingSiteExperience, useCompleteOnboardingService,
  useAdoptPreparedServices, useCompleteOnboardingSiteIntroduction, useSaveOnboardingAvailability,
  useSaveOnboardingMode, useUpdateOnboardingBusinessDetails,
} from '../../../sitePublishing/hooks';
import { useTenant } from '../../../tenancy/TenantContext';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { sitePublishingKeys } from '../../../sitePublishing/queryKeys';
import { launchActionButtonSx, NumericStepper, QuestionActions, QuestionCard, ReviewSummary } from './OnboardingPrimitives';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import pluralize from 'pluralize';
import { WorkHours } from '../../../types';
import {
  BookingChoice, CapacityBasis, ManagementStyle, resolveOnboardingMode,
} from './modeQuestionnaire';
import { formatServicePrice } from '../../../utils/servicePrice';
import { BUSINESS_TYPES, suggestedServicesFor } from '../../../sitePublishing/businessTypes';

const DAYS: Array<keyof PublicBusinessHours> =
  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = Object.fromEntries(DAYS.map((day) =>
  [day, `${day.charAt(0).toUpperCase()}${day.slice(1)}`])) as Record<keyof PublicBusinessHours, string>;

// Shows what Scheduify suggested for this step from the client's materials, and why.
export const PreparedSuggestion: React.FC<{ status: TenantOnboardingStatus; topics: RegExp }> = ({ status, topics }) => {
  const decisions = (status.prepared?.decisions ?? []).filter((decision) => topics.test(decision.topic));
  if (!decisions.length) return null;
  return <Alert icon={<AutoAwesomeRounded />} severity="info" sx={{ mb: 2.5, borderRadius: 3 }}>
    <Typography fontWeight={850}>Suggested for you</Typography>
    {decisions.map((decision) => <Typography key={decision.topic} variant="body2" sx={{ mt: .5 }}>
      <strong>{decision.choice}.</strong> {decision.reason}
    </Typography>)}
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .75 }}>
      We filled this in from your materials. Change anything that isn't right.
    </Typography>
  </Alert>;
};

const preparedQuestionnaireAnswers = (mode?: SaveMyOnboardingModeInput): {
  capacityBasis?: CapacityBasis; bookingChoice?: BookingChoice; management?: ManagementStyle;
} => {
  if (!mode) return {};
  if (mode.bookingSubject === 'resources') return { capacityBasis: 'resource' };
  if (mode.customerSelection === 'specific') return { capacityBasis: 'team_member', bookingChoice: 'specific_person' };
  return {
    capacityBasis: 'team_member',
    bookingChoice: 'business_assigned',
    management: mode.scheduleManagement === 'individual' ? 'individual_logins' : 'centralized',
  };
};

export const ModeQuestionnaire: React.FC<{ status: TenantOnboardingStatus }> = ({ status }) => {
  const modeMutation = useSaveOnboardingMode();
  const preparedMode = status.prepared?.mode;
  const preparedAnswers = preparedQuestionnaireAnswers(preparedMode);
  const businessMutation = useUpdateOnboardingBusinessDetails();
  const [stage, setStage] = useState<'business' | 'capacity' | 'customer' | 'management' | 'count' | 'terminology' | 'review'>(status.businessConfirmed ? 'capacity' : 'business');
  const [displayName, setDisplayName] = useState(status.business.displayName);
  const [timezone, setTimezone] = useState(status.business.timezone);
  const [businessType, setBusinessType] = useState(status.business.businessType ?? '');
  const [capacityBasis, setCapacityBasis] = useState<CapacityBasis | undefined>(preparedAnswers.capacityBasis);
  const [bookingChoice, setBookingChoice] = useState<BookingChoice | undefined>(preparedAnswers.bookingChoice);
  const [management, setManagement] = useState<ManagementStyle | undefined>(preparedAnswers.management);
  const [resourceCount, setResourceCount] = useState(preparedMode?.resourceCount ?? 1);
  const [singular, setSingular] = useState(preparedMode?.resourceSingular ?? 'Employee');
  const [plural, setPlural] = useState(preparedMode?.resourcePlural ?? 'Employees');
  const [customTerminology, setCustomTerminology] = useState(Boolean(preparedMode?.resourceSingular));
  const decision = capacityBasis ? (() => {
    try {
      return resolveOnboardingMode(capacityBasis, bookingChoice, management);
    } catch {
      return undefined;
    }
  })() : undefined;
  const flow = [
    'business', 'capacity',
    ...(capacityBasis === 'team_member' ? ['customer'] : []),
    ...(capacityBasis === 'team_member' && bookingChoice === 'business_assigned' ? ['management'] : []),
    ...(capacityBasis ? ['terminology'] : []),
    ...(decision?.operatingMode === 'front_desk' ? ['count'] : []),
    ...(capacityBasis ? ['review'] : []),
  ] as Array<typeof stage>;
  const titles: Record<typeof stage, string> = {
    business: 'Review your business details',
    capacity: 'When a customer books, what needs to be available?',
    customer: 'Do customers choose who serves them?',
    management: 'How will your team use Scheduify?',
    count: capacityBasis === 'resource' ? `How many ${plural.toLowerCase()} do you have?` :
      `How many ${plural.toLowerCase()} can take appointments at the same time?`,
    terminology: capacityBasis === 'resource' ? 'What do you call your bookable resources?' : 'What do you call your team members?',
    review: decision ? (decision.operatingMode === 'team' ? 'Team scheduling' : 'Front Desk scheduling') : 'Your recommended setup',
  };
  const currentIndex = Math.max(0, flow.indexOf(stage));
  const setTerminology = (nextSingular: string, nextPlural: string) => {
    setSingular(nextSingular);
    setPlural(nextPlural);
  };
  const updateSingular = (nextSingular: string) => {
    setSingular(nextSingular);
    setPlural(pluralize(nextSingular));
  };
  const saveBusiness = () => businessMutation.mutate({
    displayName: displayName.trim(), timezone,
    ...(businessType.trim() ? { businessType: businessType.trim() } : {}),
  }, { onSuccess: () => setStage('capacity') });
  const chooseCapacity = (next: CapacityBasis) => {
    setCapacityBasis(next);
    setBookingChoice(undefined);
    setManagement(undefined);
    setCustomTerminology(false);
    setTerminology(next === 'team_member' ? 'Employee' : 'Resource', next === 'team_member' ? 'Employees' : 'Resources');
  };
  const nextStage = () => {
    const index = flow.indexOf(stage);
    const next = flow[index + 1];
    if (next) setStage(next);
  };
  const previousStage = () => {
    const index = flow.indexOf(stage);
    const previous = flow[index - 1];
    if (previous) setStage(previous);
  };
  const confirm = () => {
    if (!decision) return;
    modeMutation.mutate({
      operatingMode: decision.operatingMode,
      bookingSubject: capacityBasis === 'resource' ? 'resources' : 'people',
      scheduleManagement: decision.managementStyle === 'individual_logins' ? 'individual' : 'centralized',
      customerSelection: decision.bookingChoice === 'specific_person' ? 'specific' : 'automatic',
      ...(decision.operatingMode === 'front_desk' ? { resourceCount } : {}),
      resourceSingular: singular.trim(), resourcePlural: plural.trim(),
    });
  };

  const terminologyOptions = capacityBasis === 'resource' ? [
    ['Chair', 'Chairs'], ['Room', 'Rooms'], ['Station', 'Stations'],
    ['Bay', 'Bays'], ['Vehicle', 'Vehicles'], ['Resource', 'Resources'],
  ] : [
    ['Employee', 'Employees'], ['Team member', 'Team members'], ['Barber', 'Barbers'], ['Stylist', 'Stylists'],
    ['Nail tech', 'Nail techs'], ['Technician', 'Technicians'], ['Therapist', 'Therapists'],
    ['Service provider', 'Service providers'],
  ];

  return <QuestionCard title={titles[stage]} progress={stage !== 'review' ? { current: currentIndex, total: Math.max(flow.length - 1, 1) } : undefined}
    detail={stage === 'business' ? 'Confirm these details before choosing a scheduling setup.' : undefined}>
    {stage === 'business' ? <Stack spacing={2}>
      <TextField select required label="Business type" value={businessType} onChange={(event) => setBusinessType(event.target.value)}>
        {BUSINESS_TYPES.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
      </TextField>
      <TextField label="Business name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
      <TextField select label="Timezone" value={timezone} onChange={(event) => setTimezone(event.target.value)}>
        {TIMEZONES.map((zone) => <MenuItem key={zone} value={zone}>{zone}</MenuItem>)}
      </TextField>
    </Stack> : null}
    {stage === 'capacity' ? <PreparedSuggestion status={status} topics={/scheduling|mode|front desk|team/i} /> : null}
    {stage === 'capacity' ? <ChoiceGrid>
      <ChoiceCard selected={capacityBasis === 'team_member'} icon={<Groups />} title="A team member"
        description="For businesses that schedule people, such as barbers, technicians, therapists, or consultants."
        onClick={() => chooseCapacity('team_member')} />
      <ChoiceCard selected={capacityBasis === 'resource'} icon={<MeetingRoom />} title="A workspace or piece of equipment"
        description="For businesses that schedule bays, chairs, rooms, stations, vehicles, or equipment."
        onClick={() => chooseCapacity('resource')} />
    </ChoiceGrid> : null}
    {stage === 'customer' ? <ChoiceGrid>
      <ChoiceCard selected={bookingChoice === 'specific_person'} icon={<PersonSearch />} title="Yes, customers choose someone"
        description="Customers can view your team and book with a specific person."
        onClick={() => { setBookingChoice('specific_person'); setManagement(undefined); }} />
      <ChoiceCard selected={bookingChoice === 'business_assigned'} icon={<Shuffle />} title="No, we assign whoever is available"
        description="Customers choose a service and time, and your business handles the assignment."
        onClick={() => setBookingChoice('business_assigned')} />
    </ChoiceGrid> : null}
    {stage === 'management' ? <ChoiceGrid>
      <ChoiceCard selected={management === 'individual_logins'} icon={<Groups />} title="Each team member needs a login"
        description="Everyone gets their own schedule and can manage their appointments."
        onClick={() => setManagement('individual_logins')} />
      <ChoiceCard selected={management === 'centralized'} icon={<PersonSearch />} title="One manager handles the schedule"
        description="One person manages bookings and assignments for the whole team."
        onClick={() => setManagement('centralized')} />
    </ChoiceGrid> : null}
    {stage === 'count' && decision ? <Stack spacing={2}>
      <NumericStepper label={plural}
        value={resourceCount} min={1} max={8}
        onChange={setResourceCount} />
      <Typography variant="body2" color="text.secondary">
        {capacityBasis === 'resource' ? `Count the ${plural.toLowerCase()} available for customer bookings.` :
          `Count the ${plural.toLowerCase()} available for customer bookings.`}
      </Typography>
    </Stack> : null}
    {stage === 'terminology' ? <Stack spacing={2.5}>
      <Stack direction="row" gap={1} flexWrap="wrap">
        {terminologyOptions.map(([optionSingular, optionPlural]) => <Chip key={optionSingular}
          label={optionPlural} clickable color={!customTerminology && singular === optionSingular ? 'primary' : 'default'}
          variant={!customTerminology && singular === optionSingular ? 'filled' : 'outlined'}
          onClick={() => { setCustomTerminology(false); setTerminology(optionSingular, optionPlural); }} />)}
        <Chip label="Other" clickable color={customTerminology ? 'primary' : 'default'}
          variant={customTerminology ? 'filled' : 'outlined'}
          onClick={() => { setCustomTerminology(true); setTerminology('', ''); }} />
      </Stack>
      {customTerminology ? <TextField autoFocus label={capacityBasis === 'resource' ? 'Bookable spot' : 'Team member title'} value={singular}
        helperText="Enter the singular term. We’ll pluralize it automatically."
        onChange={(event) => updateSingular(event.target.value)} /> : null}
      <Typography variant="body2" color="text.secondary">You can change this customer-facing language later.</Typography>
    </Stack> : null}
    {stage === 'review' && decision ? <Stack spacing={{ xs: 1.75, sm: 2.5 }}>
      <Box sx={{ p: { xs: 2, sm: 2.75 }, borderRadius: 3.5, border: '2px solid', borderColor: 'primary.main',
        bgcolor: 'rgba(25,118,210,.045)', boxShadow: '0 12px 28px rgba(25,118,210,.10)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center',
              bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              {decision.operatingMode === 'team' ? <Groups /> : <MeetingRoom />}
            </Box>
            <Box>
              <Typography variant="overline" color="primary.main" fontWeight={900} letterSpacing=".08em">YOUR SETUP</Typography>
              <Typography variant="h6" component="p" fontWeight={900}>{decision.operatingMode === 'team' ? 'Team scheduling' : 'Front Desk scheduling'}</Typography>
            </Box>
          </Stack>
          <Chip icon={<CheckCircle />} label="Recommended" color="primary" sx={{ display: { xs: 'none', sm: 'flex' }, fontWeight: 850 }} />
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 2, lineHeight: { xs: 1.5, sm: 1.65 }, fontSize: { xs: '.94rem', sm: '1rem' } }}>{decision.operatingMode === 'team' ?
          decision.showEmployeePicker ? 'Customers can choose a specific team member or select anyone available. Each team member gets their own schedule and login.' :
            'Customers choose a service and time, and Scheduify assigns an available team member. Each team member gets their own schedule and login.' :
          capacityBasis === 'resource' ? `Customers choose a service and time, and Scheduify reserves an available ${singular.toLowerCase()}. One manager controls the schedule.` :
            'Customers choose a service and time, and Scheduify assigns an available team member. One manager controls the schedule for everyone.'}</Typography>
        <Chip icon={<CheckCircle />} label="Recommended" color="primary" size="small"
          sx={{ display: { xs: 'inline-flex', sm: 'none' }, mt: 2, fontWeight: 850 }} />
      </Box>
      <ReviewSummary rows={[
        ...(decision.operatingMode === 'front_desk' ? [
          { label: 'Plan', value: 'Front Desk' },
        ] : []),
        { label: 'Customer booking', value: decision.showEmployeePicker ? 'Choose a team member' : 'Automatic assignment' },
        { label: 'Management', value: decision.managementStyle === 'individual_logins' ? 'Individual logins' : 'One manager' },
        ...(decision.operatingMode === 'front_desk' ? [
          { label: 'Plan capacity', value: `Up to 8 ${plural}` },
          { label: 'Starting with', value: `${resourceCount} ${resourceCount === 1 ? singular : plural}` },
        ] : []),
        { label: 'Customer term', value: plural },
      ]} />
      <Alert severity="info">{decision.operatingMode === 'team' ?
        'Your scheduling setup locks after confirmation. You’ll choose your team size and plan when you start your trial.' :
        'Your scheduling setup locks after confirmation. Review your answers before continuing.'}</Alert>
    </Stack> : null}
    {businessMutation.isError || modeMutation.isError ? <Alert severity="error">{(businessMutation.error || modeMutation.error)?.message}</Alert> : null}
    <QuestionActions onPrevious={currentIndex > 0 ? previousStage : undefined}
      onContinue={stage === 'business' ? saveBusiness : stage === 'review' ? confirm : nextStage}
      continueLabel={stage === 'review' ? 'Use this setup' : undefined}
      busy={businessMutation.isPending || modeMutation.isPending}
      disabled={stage === 'business' ? !businessType || !displayName.trim() || !timezone : stage === 'capacity' ? !capacityBasis :
        stage === 'customer' ? !bookingChoice : stage === 'management' ? !management :
          stage === 'terminology' ? !singular.trim() || !plural.trim() : stage === 'review' ? !decision : false} />
  </QuestionCard>;
};

const ChoiceGrid: React.FC<React.PropsWithChildren> = ({ children }) => <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 2 }}>{children}</Stack>;

const ChoiceCard: React.FC<{
  selected: boolean;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}> = ({ selected, icon, title, description, onClick }) => <Paper component="button" type="button" onClick={onClick}
  aria-pressed={selected} variant="outlined" sx={{ flex: 1, p: { xs: 2.25, sm: 3 }, textAlign: 'left', borderRadius: 3.5, cursor: 'pointer',
    borderWidth: 2, borderColor: selected ? 'primary.main' : 'rgba(15,23,42,.12)',
    bgcolor: selected ? 'rgba(25,118,210,.055)' : 'background.paper', color: 'text.primary', font: 'inherit',
    boxShadow: selected ? '0 5px 0 rgba(25,118,210,.22), 0 14px 32px rgba(25,118,210,.10)' : '0 4px 0 rgba(15,23,42,.08)',
    transform: selected ? 'translateY(-2px)' : 'none',
    transition: 'border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
    '&:hover': { borderColor: 'primary.main', transform: 'translateY(-3px)', boxShadow: '0 6px 0 rgba(25,118,210,.2), 0 16px 34px rgba(15,23,42,.10)' },
    '&:focus-visible': { outline: '3px solid rgba(25,118,210,.28)', outlineOffset: 3 } }}>
  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
    <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center',
      bgcolor: selected ? 'primary.main' : '#eef3f8', color: selected ? 'primary.contrastText' : 'text.secondary',
      '& svg': { fontSize: 27 } }}>{icon}</Box>
    {selected ? <CheckCircle color="primary" /> : null}
  </Box>
  <Typography variant="h6" fontWeight={900} sx={{ mt: 2, lineHeight: 1.25 }}>{title}</Typography>
  <Typography color="text.secondary" sx={{ mt: .75, lineHeight: 1.55 }}>{description}</Typography>
</Paper>;

export const GuidedAvailability: React.FC<{ status: TenantOnboardingStatus }> = ({ status }) => {
  const { tenantId } = useTenant();
  const { currentEmployeeQuery } = useAdminData();
  const queryClient = useQueryClient();
  const mutation = useSaveOnboardingAvailability();
  const [stage, setStage] = useState<'resources' | 'days' | 'firstHours' | 'weekly' | 'start'>(status.operatingMode === 'front_desk' ? 'resources' : 'days');
  const prepared = status.prepared?.availability;
  const [days, setDays] = useState<Array<keyof PublicBusinessHours>>(prepared?.days ?? []);
  const [hours, setHours] = useState<Partial<Record<keyof PublicBusinessHours, { start: string; end: string }>>>(prepared?.hours ?? {});
  // Prepared hours usually differ by day, so open the per-day view instead of copying the first day.
  const [applyAll, setApplyAll] = useState(!prepared);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [resourceNames, setResourceNames] = useState(() => prepared?.resourceNames?.length ? prepared.resourceNames :
    [`${status.resourceSingular ?? 'Resource'} 1`]);
  const firstDay = days[0];
  const firstHours = firstDay ? hours[firstDay] ?? { start: '09:00', end: '17:00' } : { start: '09:00', end: '17:00' };
  const setDayHours = (day: keyof PublicBusinessHours, key: 'start' | 'end', value: string) => setHours((current) => ({ ...current, [day]: { ...(current[day] ?? { start: '09:00', end: '17:00' }), [key]: value } }));
  const continueHours = () => {
    if (applyAll) {
      setHours((current) => Object.fromEntries(days.map((day) => [day, current[firstDay] ?? firstHours])));
      setStage('start');
    } else setStage('weekly');
  };
  const submit = () => mutation.mutate({ days, hours, bookingsStartDate: startDate, resourceNames,
    ...(prepared?.generalBreak ? { generalBreak: prepared.generalBreak } : {}),
    ...(prepared?.breaks ? { breaks: prepared.breaks } : {}) }, {
    onSuccess: async () => {
      const closed = '1'.repeat(288);
      const template = DAYS.reduce((result, day) => {
        const range = hours[day];
        if (!days.includes(day) || !range) {
          result[day] = closed;
          return result;
        }
        const indexFor = (value: string) =>
          (Number(value.slice(0, 2)) * 60 + Number(value.slice(3))) / 5;
        const start = indexFor(range.start);
        const end = indexFor(range.end);
        result[day] = `${'1'.repeat(start)}${'0'.repeat(end - start)}${'1'.repeat(288 - end)}`;
        return result;
      }, {} as WorkHours);
      if (status.operatingMode === 'team' && currentEmployeeQuery.data?.id) {
        queryClient.setQueryData(['employeeWorkHours', tenantId, currentEmployeeQuery.data.id], template);
      } else if (status.operatingMode === 'front_desk') {
        resourceNames.forEach((_, index) => {
          queryClient.setQueryData(['employeeWorkHours', tenantId, `onboarding-resource-${index + 1}`], template);
        });
        await queryClient.refetchQueries({ queryKey: ['employees', tenantId], exact: true });
      }
      await queryClient.invalidateQueries({ queryKey: ['employeeWorkHours', tenantId] });
    },
  });
  const title = stage === 'resources' ? `Name your ${status.resourcePlural?.toLowerCase() ?? 'resources'}` : stage === 'days' ?
    'What days are you available to be booked?' : stage === 'firstHours' ? `What hours work on ${DAY_LABELS[firstDay]}?` :
      stage === 'weekly' ? 'Set hours for each available day' : 'When should customers start booking?';
  const stages = status.operatingMode === 'front_desk' ? ['resources', 'days', 'firstHours', 'weekly', 'start'] : ['days', 'firstHours', 'weekly', 'start'];
  return <QuestionCard title={title} progress={{ current: Math.max(0, stages.indexOf(stage)), total: stages.length }}>
    {stage === stages[0] ? <PreparedSuggestion status={status} topics={/hours|availability|resource|chair|room|station/i} /> : null}
    {stage === 'resources' ? <Stack spacing={2}>
      {resourceNames.map((name, index) => <Stack key={index} direction="row" spacing={1} alignItems="center">
        <TextField fullWidth autoFocus={index === 0} label={`${status.resourceSingular ?? 'Resource'} ${index + 1}`}
          value={name} onChange={(event) => setResourceNames((current) =>
            current.map((entry, item) => item === index ? event.target.value : entry))} />
        {index > 0 ? <Button aria-label={`Remove ${status.resourceSingular ?? 'resource'} ${index + 1}`}
          onClick={() => setResourceNames((current) => current.filter((_, item) => item !== index))}
          sx={{ minWidth: 46, width: 46, height: 46, borderRadius: '50%', p: 0 }}><CloseRounded /></Button> : null}
      </Stack>)}
      {resourceNames.length < 8 ? <Button variant="outlined" startIcon={<AddRounded />}
        onClick={() => setResourceNames((current) => [...current, `${status.resourceSingular ?? 'Resource'} ${current.length + 1}`])}
        sx={{ alignSelf: 'flex-start', borderRadius: 999, px: 2.25, textTransform: 'none', fontWeight: 850 }}>
        Add another
      </Button> : null}
      <Typography variant="body2" color="text.secondary">Add only the resources you want available now. You can have up to eight.</Typography>
    </Stack> : null}
    {stage === 'days' ? <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.25 }}>{DAYS.map((day) => <Button key={day} variant={days.includes(day) ? 'contained' : 'outlined'} aria-pressed={days.includes(day)} onClick={() => setDays((current) => current.includes(day) ? current.filter((entry) => entry !== day) : DAYS.filter((entry) => [...current, day].includes(entry)))}
      sx={{ minHeight: 58, borderRadius: 3, borderWidth: 2, fontWeight: 850, boxShadow: days.includes(day) ? '0 4px 0 #0d55a4' : '0 3px 0 rgba(15,23,42,.10)' }}>{DAY_LABELS[day]}</Button>)}</Box> : null}
    {stage === 'firstHours' ? <Stack spacing={2}><TimeRow day={firstDay} value={firstHours} onChange={setDayHours} />{days.length > 1 ? <FormControlLabel control={<Checkbox checked={applyAll} onChange={(event) => setApplyAll(event.target.checked)} />} label="Use these hours for all selected days" /> : null}</Stack> : null}
    {stage === 'weekly' ? <Stack spacing={2}>{days.map((day) => <TimeRow key={day} day={day} value={hours[day] ?? { start: '09:00', end: '17:00' }} onChange={setDayHours} />)}</Stack> : null}
    {stage === 'start' ? <TextField type="date" label="Bookings start" value={startDate} InputLabelProps={{ shrink: true }} inputProps={{ min: new Date().toISOString().slice(0, 10) }} onChange={(event) => setStartDate(event.target.value)} /> : null}
    {mutation.isError ? <Alert severity="error">{mutation.error.message}</Alert> : null}
    <QuestionActions onPrevious={stage === 'resources' || (stage === 'days' && status.operatingMode !== 'front_desk') ? undefined : () => setStage(stage === 'days' ? 'resources' : stage === 'firstHours' ? 'days' : stage === 'weekly' ? 'firstHours' : applyAll ? 'firstHours' : 'weekly')}
      onContinue={stage === 'resources' ? () => setStage('days') : stage === 'days' ? () => setStage('firstHours') : stage === 'firstHours' ? continueHours : stage === 'weekly' ? () => setStage('start') : submit}
      continueLabel={stage === 'start' ? 'Save availability' : undefined} busy={mutation.isPending}
      disabled={stage === 'resources' ? resourceNames.some((name) => !name.trim()) : stage === 'days' ? !days.length : stage === 'firstHours' ? firstHours.start >= firstHours.end : stage === 'weekly' ? days.some((day) => !hours[day] || hours[day]!.start >= hours[day]!.end) : false} />
  </QuestionCard>;
};

const TimeRow: React.FC<{ day: keyof PublicBusinessHours; value: { start: string; end: string }; onChange: (day: keyof PublicBusinessHours, key: 'start' | 'end', value: string) => void }> = ({ day, value, onChange }) => <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
  <Typography fontWeight={750} sx={{ minWidth: 100 }}>{DAY_LABELS[day]}</Typography>
  <TimePicker label="Start" minutesStep={5} value={dayjs(`2000-01-01T${value.start}`)} onChange={(next) => next && onChange(day, 'start', next.format('HH:mm'))} />
  <TimePicker label="End" minutesStep={5} value={dayjs(`2000-01-01T${value.end}`)} onChange={(next) => next && onChange(day, 'end', next.format('HH:mm'))} />
</Stack>;

// The services prepared from the client's price list, added in one step.
const PreparedServices: React.FC<{ status: TenantOnboardingStatus; onEditFirst: () => void }> = ({ status, onEditFirst }) => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const adoptMutation = useAdoptPreparedServices();
  const servicesQuery = useQuery({ queryKey: ['services', tenantId], queryFn: () => fetchServices(tenantId), enabled: Boolean(tenantId) });
  const prepared = (servicesQuery.data ?? []).filter((service) => service.prepared);
  const adopt = () => adoptMutation.mutate(undefined, {
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['services', tenantId] }); },
  });
  return <QuestionCard title="Your services are ready" detail={`We set up ${status.prepared?.serviceCount ?? prepared.length} services from your price list. Add them all now, or create your first one yourself.`}>
    <PreparedSuggestion status={status} topics={/service|price|menu/i} />
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      {servicesQuery.isLoading ? <Box sx={{ p: 2 }}><Typography color="text.secondary">Loading services…</Typography></Box> :
        prepared.map((service, index) => <Stack key={service.id} direction="row" justifyContent="space-between" spacing={2}
          sx={{ px: 2, py: 1.5, borderTop: index ? '1px solid' : 'none', borderColor: 'divider' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={850}>{service.title}</Typography>
            <Typography variant="body2" color="text.secondary">{service.time} min{service.category ? ` · ${service.category}` : ''}</Typography>
          </Box>
          <Typography fontWeight={850} sx={{ whiteSpace: 'nowrap' }}>{formatServicePrice(service)}</Typography>
        </Stack>)}
    </Paper>
    {adoptMutation.isError ? <Alert severity="error" sx={{ mt: 2 }}>{adoptMutation.error.message}</Alert> : null}
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
      <Button variant="contained" size="large" onClick={adopt} disabled={adoptMutation.isPending || !prepared.length}
        sx={{ ...launchActionButtonSx, px: 4 }}>
        {adoptMutation.isPending ? 'Adding services…' : 'Use these services'}
      </Button>
      <Button size="large" onClick={onEditFirst} disabled={adoptMutation.isPending} sx={{ ...launchActionButtonSx, fontWeight: 850 }}>
        Create my own instead
      </Button>
    </Stack>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
      You can edit prices, times and photos any time in Services.
    </Typography>
  </QuestionCard>;
};

export const FirstServiceWizard: React.FC<{ status: TenantOnboardingStatus }> = ({ status }) => {
  const [createOwn, setCreateOwn] = useState(false);
  if (status.prepared?.serviceCount && !createOwn) return <PreparedServices status={status} onEditFirst={() => setCreateOwn(true)} />;
  return <FirstServiceQuestions status={status} />;
};

const FirstServiceQuestions: React.FC<{ status: TenantOnboardingStatus }> = ({ status }) => {
  const { tenantId } = useTenant();
  const { currentEmployeeQuery } = useAdminData();
  const queryClient = useQueryClient();
  const completeMutation = useCompleteOnboardingService();
  const [question, setQuestion] = useState(0);
  const [name, setName] = useState('');
  const [duration, setDuration] = useState(60);
  const [price, setPrice] = useState('');
  const [priceVaries, setPriceVaries] = useState(false);
  const [description, setDescription] = useState('');
  const [image, setImage] = useState<File>();
  const [visible, setVisible] = useState(false);
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const serviceSuggestions = suggestedServicesFor(status.business.businessType);
  const businessTypeLabel = status.business.businessType && status.business.businessType !== 'Other'
    ? status.business.businessType
    : 'your business';
  const titles = ['What is your first service called?', 'How long does it take?', 'What does it cost?', 'Describe the service', 'Show off your service', 'Organize it with a category'];
  const submit = async () => {
    setSaving(true); setError(undefined);
    try {
      const employees = status.operatingMode === 'front_desk' ? Array.from({ length: status.resourceCount ?? 0 }, (_, index) => `onboarding-resource-${index + 1}`) : currentEmployeeQuery.data?.id ? [currentEmployeeQuery.data.id] : [];
      if (!employees.length) throw new Error('No bookable resource is available.');
      const serviceId = await addService({ title: name.trim(), time: duration, price: priceVaries ? 0 : Number(price), priceVaries, questions: [], description: description.trim(), employees, showOnHomepage: Boolean(image) && visible }, image, tenantId);
      await completeMutation.mutateAsync({ serviceId, ...(category.trim() ? { category: category.trim() } : {}) });
      await queryClient.invalidateQueries({ queryKey: ['services', tenantId] });
      await queryClient.invalidateQueries({ queryKey: sitePublishingKeys.onboarding(tenantId) });
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Could not create the service.'); }
    finally { setSaving(false); }
  };
  return <QuestionCard title={titles[question]} progress={{ current: question, total: titles.length }} detail={question === 0 ? `Choose a popular service for ${businessTypeLabel}, or enter your own.` : question === 3 ? 'Descriptions help customers understand what is included before they book.' : question === 4 ? 'Add an image to showcase this service on your homepage. You can skip this for now.' : question === 5 ? 'Categories make larger service lists easier to browse. You can skip this for now.' : undefined}>
    {question === 0 ? <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1.25 }}>Popular choices</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {serviceSuggestions.map((suggestion) => <Button key={suggestion} variant={name === suggestion ? 'contained' : 'outlined'}
            aria-pressed={name === suggestion} onClick={() => setName(suggestion)}
            sx={{ minHeight: 42, borderRadius: 999, px: 2, borderWidth: 2, fontWeight: 800, textTransform: 'none' }}>
            {suggestion}
          </Button>)}
        </Box>
      </Box>
      <TextField label="Or enter your own service" value={name} onChange={(event) => setName(event.target.value)} />
    </Stack> : null}
    {question === 1 ? <NumericStepper label="Minutes" value={duration} min={5} max={480} step={5} onChange={setDuration} /> : null}
    {question === 2 ? <Stack spacing={1.5}>
      {priceVaries ? null : <TextField type="number" label="Price" value={price} inputProps={{ min: 0, step: '0.01' }} onChange={(event) => setPrice(event.target.value)} />}
      <FormControlLabel control={<Checkbox checked={priceVaries} onChange={(event) => setPriceVaries(event.target.checked)} />}
        label={<Box><Typography fontWeight={800}>Price varies</Typography><Typography variant="body2" color="text.secondary">Customers see “Price varies” and pay at the visit. You set the final price on each booking.</Typography></Box>} />
    </Stack> : null}
    {question === 3 ? <TextField multiline minRows={4} label="Description (optional)" value={description} onChange={(event) => setDescription(event.target.value)} /> : null}
    {question === 4 ? <Stack spacing={2}>
      <Button component="label" variant="outlined" sx={{ minHeight: 132, borderRadius: 3.5, borderWidth: 2, borderStyle: 'dashed', fontWeight: 850 }}>
        <Stack spacing={1} alignItems="center">
          <ImageRounded color="primary" sx={{ fontSize: 34 }} />
          <Typography fontWeight={900}>{image ? 'Change image' : 'Choose an image'}</Typography>
          <Typography variant="caption" color="text.secondary">{image ? image.name : 'JPG, PNG, or WebP'}</Typography>
        </Stack>
        <input hidden type="file" accept="image/*" onChange={(event) => {
          const file = event.target.files?.[0];
          if (file && !file.type.startsWith('image/')) setError('Choose a valid image file.');
          else { setImage(file); setVisible(Boolean(file)); setError(undefined); }
        }} />
      </Button>
      {image ? <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(25,118,210,.04)', borderColor: 'rgba(25,118,210,.2)' }}>
        <FormControlLabel sx={{ m: 0, alignItems: 'flex-start' }} control={<Checkbox checked={visible} onChange={(event) => setVisible(event.target.checked)} />}
          label={<Box sx={{ pt: .5 }}><Typography fontWeight={850}>Feature this service on my homepage</Typography><Typography variant="body2" color="text.secondary">Customers will see it while exploring your website.</Typography></Box>} />
      </Paper> : null}
    </Stack> : null}
    {question === 5 ? <TextField label="Category (optional)" value={category} onChange={(event) => setCategory(event.target.value)} /> : null}
    {error || completeMutation.isError ? <Alert severity="error">{error || completeMutation.error?.message}</Alert> : null}
    <QuestionActions onPrevious={question ? () => setQuestion((value) => value - 1) : undefined}
      onContinue={question === titles.length - 1 ? submit : () => setQuestion((value) => value + 1)}
      continueLabel={question === titles.length - 1 ? 'Add service' : undefined}
      busy={saving || completeMutation.isPending} disabled={question === 0 ? !name.trim() : question === 2 ? !priceVaries && (price === '' || Number(price) < 0) : false} />
  </QuestionCard>;
};

export const SiteExperienceChoice: React.FC<{ status: TenantOnboardingStatus }> = ({ status }) => {
  const choiceMutation = useChooseOnboardingSiteExperience();
  const introMutation = useCompleteOnboardingSiteIntroduction();
  const [page, setPage] = useState(0);
  const intro = [
    ['Your public website is included', 'Give customers one place to learn about your business and start booking.'],
    ['Make it yours with Site Builder', 'Edit your brand, content, images, hours, and contact details in a live preview.'],
    ['You can start with booking only', 'Launch a focused booking experience now and build the full website later.'],
    ['How would you like to start?', 'Build a complete website now, or start with a focused booking experience and customize later. Support is available at support@scheduify.com.'],
  ];
  const chooseExperience = async (experience: 'full_site' | 'booking_only') => {
    try {
      await introMutation.mutateAsync();
      choiceMutation.mutate(experience);
    } catch {
      // The mutation surfaces its error in the question card.
    }
  };
  if (!status.siteIntroductionComplete) return <QuestionCard title={intro[page][0]} detail={intro[page][1]} progress={{ current: page, total: intro.length }}>
    {page === 1 || page === 2 ? <Box role="img" aria-label={page === 1 ? 'Site Builder demonstration coming soon' : 'Booking-only site preview'} sx={{ minHeight: { xs: 220, sm: 300 }, borderRadius: 3, bgcolor: page === 1 ? 'grey.100' : '#eff6ff', border: '1px solid', borderColor: page === 1 ? 'divider' : 'rgba(25,118,210,.25)', display: 'grid', placeItems: 'center', p: 3 }}><Typography variant="h6" fontWeight={800}>{page === 1 ? 'Site Builder preview' : 'Booking-only site preview'}</Typography></Box> : null}
    {page === intro.length - 1 ? <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Button variant="contained" size="large" sx={launchActionButtonSx} onClick={() => void chooseExperience('full_site')} disabled={introMutation.isPending || choiceMutation.isPending}>Build my website · about 15–20 minutes</Button>
        <Button variant="outlined" size="large" sx={launchActionButtonSx} onClick={() => void chooseExperience('booking_only')} disabled={introMutation.isPending || choiceMutation.isPending}>Use booking only</Button>
      </Stack>
      <QuestionActions onPrevious={() => setPage((value) => value - 1)} busy={introMutation.isPending || choiceMutation.isPending} />
    </Stack> : <QuestionActions onPrevious={page ? () => setPage((value) => value - 1) : undefined}
      onContinue={() => setPage((value) => value + 1)} busy={introMutation.isPending || choiceMutation.isPending} />}
    {introMutation.isError || choiceMutation.isError ? <Alert severity="error">{introMutation.error?.message || choiceMutation.error?.message}</Alert> : null}
  </QuestionCard>;
  if (status.siteExperience === 'booking_only' && status.gates.sitePublished.complete) return <QuestionCard title="Your booking experience is prepared" detail="Customers will go directly to booking. You can build the complete website now or after launch.">
    <Button variant="outlined" sx={{ ...launchActionButtonSx, alignSelf: { xs: 'stretch', sm: 'flex-start' } }} onClick={() => choiceMutation.mutate('full_site')} disabled={choiceMutation.isPending}>Build full website instead</Button>
    {choiceMutation.isError ? <Alert severity="error">{choiceMutation.error.message}</Alert> : null}
  </QuestionCard>;
  return <QuestionCard title="How would you like to start?" detail="Build a complete website now, or start with a focused booking experience and customize later.">
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <Button variant="contained" size="large" sx={launchActionButtonSx} onClick={() => choiceMutation.mutate('full_site')} disabled={choiceMutation.isPending}>Build my website · about 15–20 minutes</Button>
      <Button variant="outlined" size="large" sx={launchActionButtonSx} onClick={() => choiceMutation.mutate('booking_only')} disabled={choiceMutation.isPending}>Skip for now</Button>
    </Stack>
    {choiceMutation.isError ? <Alert severity="error">{choiceMutation.error.message}</Alert> : null}
    <QuestionActions />
  </QuestionCard>;
};
